/**
 * Project Data Helpers
 * Utility functions for analyzing and formatting MS Project data
 */

/**
 * Analyze project health and identify issues
 * @param {Object} projectData - Project data from MS Project
 * @returns {Object} Analysis results
 */
export function analyzeProjectHealth(projectData) {
  const tasks = projectData.tasks || [];
  const resources = projectData.resources || [];
  const assignments = projectData.assignments || [];

  const analysis = {
    overall_health: "good",
    issues: [],
    warnings: [],
    recommendations: [],
    statistics: {
      total_tasks: tasks.length,
      total_resources: resources.length,
      total_assignments: assignments.length,
      completed_tasks: 0,
      critical_tasks: 0,
      overdue_tasks: 0,
      over_allocated_resources: 0,
      under_allocated_resources: 0,
      total_cost: 0,
      actual_cost: 0,
      remaining_cost: 0,
    },
  };

  // Analyze tasks
  tasks.forEach((task) => {
    // Count completed tasks
    if (task.percentComplete >= 100) {
      analysis.statistics.completed_tasks++;
    }

    // Count critical tasks
    if (task.critical) {
      analysis.statistics.critical_tasks++;
    }

    // Check for overdue tasks
    if (task.finish && new Date(task.finish) < new Date() && task.percentComplete < 100) {
      analysis.statistics.overdue_tasks++;
      analysis.issues.push({
        type: "overdue_task",
        severity: "high",
        message: `Task "${task.name}" is overdue (due: ${new Date(task.finish).toLocaleDateString()})`,
        task_id: task.id,
        task_name: task.name,
      });
    }

    // Cost analysis
    if (task.cost) analysis.statistics.total_cost += task.cost;
    if (task.actualCost) analysis.statistics.actual_cost += task.actualCost;
    if (task.remainingCost) analysis.statistics.remaining_cost += task.remainingCost;

    // Check for tasks without resources
    if (!task.resourceNames || task.resourceNames.trim() === "") {
      analysis.warnings.push({
        type: "unassigned_task",
        severity: "medium",
        message: `Task "${task.name}" has no resource assignments`,
        task_id: task.id,
        task_name: task.name,
      });
    }

    // Check for tasks without predecessors (except summary tasks)
    if (task.outlineLevel === 1 && (!task.predecessors || task.predecessors.trim() === "")) {
      analysis.warnings.push({
        type: "missing_predecessors",
        severity: "low",
        message: `Summary task "${task.name}" has no predecessor dependencies`,
        task_id: task.id,
        task_name: task.name,
      });
    }
  });

  // Resource analysis removed

  // Determine overall health
  if (analysis.issues.some((issue) => issue.severity === "high")) {
    analysis.overall_health = "critical";
  } else if (analysis.issues.length > 0 || analysis.warnings.length > 5) {
    analysis.overall_health = "warning";
  }

  // Generate recommendations
  if (analysis.statistics.overdue_tasks > 0) {
    analysis.recommendations.push(
      "Review and update overdue tasks. Consider adjusting deadlines or reallocating resources."
    );
  }

  if (analysis.statistics.critical_tasks > analysis.statistics.total_tasks * 0.3) {
    analysis.recommendations.push(
      "High number of critical tasks detected. Consider breaking down tasks or adding buffer time."
    );
  }

  return analysis;
}

/**
 * Generate project timeline analysis
 * @param {Object} projectData - Project data from MS Project
 * @returns {Object} Timeline analysis
 */
export function analyzeProjectTimeline(projectData) {
  const tasks = projectData.tasks || [];
  const projectStart = projectData.properties?.ProjectStartDate;
  const projectFinish = projectData.properties?.ProjectFinishDate;

  const timeline = {
    project_duration_days: 0,
    actual_duration_days: 0,
    remaining_duration_days: 0,
    completion_percentage: 0,
    critical_path_length: 0,
    buffer_time_days: 0,
    milestones: [],
    phase_breakdown: {},
    risk_factors: [],
  };

  if (projectStart && projectFinish) {
    const startDate = new Date(projectStart);
    const finishDate = new Date(projectFinish);
    timeline.project_duration_days = Math.ceil((finishDate - startDate) / (1000 * 60 * 60 * 24));
  }

  // Analyze task durations
  let totalActualDuration = 0;
  let totalRemainingDuration = 0;
  let completedTasks = 0;

  tasks.forEach((task) => {
    if (task.duration) {
      if (task.percentComplete >= 100) {
        completedTasks++;
        totalActualDuration += task.duration;
      } else {
        const remainingPercent = (100 - task.percentComplete) / 100;
        totalActualDuration += task.duration * (task.percentComplete / 100);
        totalRemainingDuration += task.duration * remainingPercent;
      }
    }

    // Identify milestones (tasks with 0 duration or specific markers)
    if (task.duration === 0 || task.name.toLowerCase().includes("milestone")) {
      timeline.milestones.push({
        name: task.name,
        date: task.finish,
        status: task.percentComplete >= 100 ? "completed" : "pending",
      });
    }
  });

  timeline.actual_duration_days = totalActualDuration;
  timeline.remaining_duration_days = totalRemainingDuration;
  timeline.completion_percentage = tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0;

  // Calculate buffer time
  if (timeline.project_duration_days > 0) {
    timeline.buffer_time_days =
      timeline.project_duration_days -
      timeline.actual_duration_days -
      timeline.remaining_duration_days;
  }

  // Risk analysis
  if (timeline.buffer_time_days < 0) {
    timeline.risk_factors.push({
      type: "schedule_overrun",
      severity: "high",
      message: `Project is ${Math.abs(timeline.buffer_time_days)} days behind schedule`,
    });
  }

  if (
    timeline.completion_percentage < 50 &&
    timeline.remaining_duration_days > timeline.project_duration_days * 0.5
  ) {
    timeline.risk_factors.push({
      type: "low_progress",
      severity: "medium",
      message: "Low completion percentage with significant work remaining",
    });
  }

  return timeline;
}

/**
 * Format project data for chat context
 * @param {Object} projectData - Raw project data
 * @returns {Object} Formatted data for AI context
 */
export function formatProjectDataForChat(projectData) {
  const summary = {
    project_info: {
      name: projectData.properties?.name || "Unknown Project",
      title: projectData.properties?.ProjectTitle || "",
      author: projectData.properties?.ProjectAuthor || "",
      start_date: projectData.properties?.ProjectStartDate || null,
      finish_date: projectData.properties?.ProjectFinishDate || null,
      percent_complete: projectData.properties?.ProjectPercentComplete || 0,
      total_cost: projectData.properties?.ProjectCost || 0,
      total_work: projectData.properties?.ProjectWork || 0,
    },
    task_summary: {
      total_tasks: projectData.tasks?.length || 0,
      completed_tasks: projectData.tasks?.filter((t) => t.percentComplete >= 100).length || 0,
      critical_tasks: projectData.tasks?.filter((t) => t.critical).length || 0,
      overdue_tasks:
        projectData.tasks?.filter((t) => {
          if (!t.finish) return false;
          return new Date(t.finish) < new Date() && t.percentComplete < 100;
        }).length || 0,
    },

    key_metrics: {
      project_duration: projectData.properties?.ProjectDuration || 0,
      actual_cost: projectData.properties?.ProjectActualCost || 0,
      remaining_cost: projectData.properties?.ProjectActualCost || 0,
      budget_variance: 0,
    },
  };

  // Calculate budget variance
  if (summary.key_metrics.actual_cost > 0 && summary.project_info.total_cost > 0) {
    summary.key_metrics.budget_variance =
      summary.key_metrics.actual_cost - summary.project_info.total_cost;
  }

  return summary;
}

/**
 * Generate project insights for AI assistance
 * @param {Object} projectData - Project data from MS Project
 * @returns {Array} Array of insights
 */
export function generateProjectInsights(projectData) {
  const insights = [];
  const tasks = projectData.tasks || [];

  // Project completion insights
  const completionRate = projectData.properties?.ProjectPercentComplete || 0;
  if (completionRate > 0) {
    insights.push({
      type: "progress",
      message: `Project is ${completionRate}% complete`,
      priority: completionRate < 25 ? "high" : completionRate < 50 ? "medium" : "low",
    });
  }

  // Task insights
  const overdueTasks = tasks.filter((t) => {
    if (!t.finish) return false;
    return new Date(t.finish) < new Date() && t.percentComplete < 100;
  });

  if (overdueTasks.length > 0) {
    insights.push({
      type: "schedule",
      message: `${overdueTasks.length} tasks are overdue and need attention`,
      priority: "high",
      affected_tasks: overdueTasks.map((t) => ({ id: t.id, name: t.name })),
    });
  }

  // Resource insights removed

  // Critical path insights
  const criticalTasks = tasks.filter((t) => t.critical);
  if (criticalTasks.length > 0) {
    insights.push({
      type: "critical_path",
      message: `${criticalTasks.length} tasks are on the critical path`,
      priority: "medium",
      note: "Any delays in critical tasks will impact the project timeline",
    });
  }

  return insights;
}

export default {
  analyzeProjectHealth,
  analyzeProjectTimeline,
  formatProjectDataForChat,
  generateProjectInsights,
};
